/**
 * First-login "Responsible Use" reminder gate (Task #315).
 *
 * A blocking, non-dismissible reminder that the user must explicitly
 * acknowledge before they can use the Dashboard (or any other
 * authenticated page — `Layout` mounts the gate, and `Layout` wraps
 * every authenticated route).
 *
 * Behaviour:
 *  - Renders when the authenticated user's
 *    `responsibleUseAcknowledged` flag is anything other than `true`.
 *  - No close affordances: no X, Escape is ignored, click-outside is
 *    ignored. Same pattern as `LegalConsentGate` so the two gates feel
 *    consistent.
 *  - The "I understand, continue" button stays disabled until the
 *    acknowledgement toggle is on.
 *  - "Cancel" logs the user out and returns them to the login screen.
 *  - On success we invalidate the session query so the gate disappears
 *    app-wide. On failure we show an inline error and leave the modal
 *    open without flipping the local toggle.
 *  - Refreshing the page resets the local toggle to off (state is not
 *    persisted client-side), per the acceptance criteria.
 *
 * NOT a legal contract — the binding ToS / Privacy acceptance lives in
 * the signup flow + `LegalConsentGate`. This is a friendly reminder.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import {
  useLogout,
  useAcknowledgeResponsibleUse,
  getGetSessionQueryKey,
} from "@workspace/api-client-react";

export function ResponsibleUseGate() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const { mutateAsync: logout } = useLogout();

  const needsAcknowledgement =
    isAuthenticated &&
    !isLoading &&
    user != null &&
    user.responsibleUseAcknowledged !== true;

  const [acknowledged, setAcknowledged] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Whenever the gate becomes visible (or the user identity changes),
  // reset the local toggle so it always starts off, per spec.
  useEffect(() => {
    if (needsAcknowledgement) {
      setAcknowledged(false);
      setErrorMessage(null);
    }
  }, [needsAcknowledgement, user?.id]);

  // Lock body scroll while the modal is open so the page behind can't be
  // operated via keyboard scrolling either.
  useEffect(() => {
    if (!needsAcknowledgement) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [needsAcknowledgement]);

  // Swallow Escape so the browser / any ancestor handler can't dismiss
  // the gate while it's open. Capture-phase so we run before anything
  // beneath the overlay.
  useEffect(() => {
    if (!needsAcknowledgement) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [needsAcknowledgement]);

  const acknowledgeMutation = useAcknowledgeResponsibleUse({
    mutation: {
      onSuccess: async () => {
        // Refresh the session so `user.responsibleUseAcknowledged`
        // flips true and the gate unmounts everywhere.
        await queryClient.invalidateQueries({
          queryKey: getGetSessionQueryKey(),
        });
      },
      onError: () => {
        setErrorMessage("Something went wrong. Please try again.");
      },
    },
  });

  const handleContinue = () => {
    if (!acknowledged || acknowledgeMutation.isPending) return;
    setErrorMessage(null);
    acknowledgeMutation.mutate();
  };

  const handleCancel = async () => {
    try {
      await logout();
    } catch {
      /* ignore — clear local state regardless */
    }
    queryClient.clear();
    window.location.href = `${import.meta.env.BASE_URL}login`;
  };

  if (!needsAcknowledgement) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="responsible-use-title"
      aria-describedby="responsible-use-body"
      data-testid="responsible-use-gate"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/65 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg border border-border bg-card shadow-2xl">
        <div className="p-6 space-y-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2
                id="responsible-use-title"
                className="text-lg font-semibold leading-tight"
              >
                A Quick Reminder
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Before you start creating
              </p>
            </div>
          </div>

          <div
            id="responsible-use-body"
            className="text-sm text-foreground/90 space-y-3 leading-relaxed"
          >
            <p>
              A quick reminder before you start. You&apos;ve already agreed to
              our Terms of Service and Privacy Policy at signup — this is just
              a friendly heads-up on the things that matter most when creating
              AI content, because this space moves fast and a little awareness
              goes a long way.
            </p>
            <p>
              Laws around AI-generated content vary from country to country,
              and the rules can be stricter when it comes to advertising,
              endorsements, or content featuring real people. This area is
              still evolving, so it&apos;s worth keeping an eye on what applies
              where you live and where you publish.
            </p>
            <p>
              <strong>
                Please only create images of real people when you have their
                permission.
              </strong>{" "}
              That goes for celebrities, public figures, friends, family,
              colleagues — anyone. A quick &ldquo;yes&rdquo; from them goes a
              long way.
            </p>
            <p>
              <strong>Only upload reference content that&apos;s yours to use.</strong>{" "}
              Your own photos, properly licensed images, or content you have
              permission to use. Avoid uploading things owned by other people,
              brands, or rights holders without their okay.
            </p>
            <p>
              When your AI-generated content could be mistaken for real
              footage, <strong>let people know it&apos;s AI</strong> — a note
              in the caption or on-screen does the trick. Different platforms
              and regions have their own labeling rules, so it&apos;s worth a
              quick check before you publish.
            </p>
            <p>
              You&apos;re the creator here, and you&apos;re in charge of what
              you put out into the world. Have fun, be thoughtful, and create
              things you&apos;re proud of.
            </p>
          </div>

          <div className="flex items-start gap-3 rounded-md border border-border bg-muted/20 p-3">
            <Switch
              id="responsible-use-confirm"
              checked={acknowledged}
              onCheckedChange={(v) => {
                setAcknowledged(v === true);
                if (v) setErrorMessage(null);
              }}
              disabled={acknowledgeMutation.isPending}
              data-testid="responsible-use-toggle"
              className="mt-0.5"
            />
            <Label
              htmlFor="responsible-use-confirm"
              className="text-sm font-normal leading-snug cursor-pointer"
            >
              Got it — I&apos;ll create thoughtfully and responsibly
            </Label>
          </div>

          {errorMessage && (
            <p
              className="text-sm text-destructive"
              role="alert"
              data-testid="responsible-use-error"
            >
              {errorMessage}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              data-testid="responsible-use-cancel"
            >
              Cancel and Logout
            </Button>
            <Button
              type="button"
              onClick={handleContinue}
              disabled={!acknowledged || acknowledgeMutation.isPending}
              data-testid="responsible-use-continue"
            >
              {acknowledgeMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              I understand, continue
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center pt-1">
            By continuing you confirm you&apos;ve read our{" "}
            <a
              href={`${import.meta.env.BASE_URL}privacy`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
              data-testid="responsible-use-privacy-link"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href={`${import.meta.env.BASE_URL}terms`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
              data-testid="responsible-use-terms-link"
            >
              Terms of Service
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
